import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";

import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenMobileSidenav,
} from "context";

function formatRouteTitle(pathname) {
  const routePart = pathname.split("/").filter(Boolean).pop() || "dashboard";
  return routePart.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function DashboardNavbar({ title }) {
  const location = useLocation();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, openMobileSidenav } = controller;
  const adminDetails = useMemo(() => {
    const rawValue = localStorage.getItem("admin_details");
    return rawValue ? JSON.parse(rawValue) : {};
  }, []);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1200 : false
  );

  const computedTitle = title || formatRouteTitle(location.pathname);
  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  useEffect(() => {
    const syncViewport = () => {
      setIsMobile(window.innerWidth < 1200);
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);

    return () => window.removeEventListener("resize", syncViewport);
  }, []);

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        top: 0,
        mb: 3,
        borderRadius: { xs: "18px", md: "24px" },
        border: "1px solid rgba(126, 170, 220, 0.12)",
        boxShadow: "0 18px 34px rgba(0,0,0,0.24)",
        backdropFilter: "blur(22px)",
        background: "rgba(9, 18, 31, 0.82)",
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: "68px !important", md: "76px !important" },
          px: { xs: "12px !important", md: "18px !important" },
          gap: 1.25,
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1.25}
          width={{ xs: "100%", sm: "auto" }}
        >
          <IconButton
            onClick={() => {
              if (isMobile) {
                setOpenMobileSidenav(dispatch, !openMobileSidenav);
                return;
              }

              setMiniSidenav(dispatch, !miniSidenav);
            }}
            sx={{
              width: 44,
              height: 44,
              borderRadius: "14px",
              background: "rgba(94,168,255,0.12)",
              color: "#dce9ff",
              "&:hover": {
                background: "rgba(94,168,255,0.2)",
              },
            }}
          >
            <Icon>
              {isMobile ? (openMobileSidenav ? "close" : "menu") : miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </IconButton>
          <Box>
            <Typography fontSize="0.78rem" fontWeight={700} color="#8fa7c4">
              AgriQuiz Admin
            </Typography>
            <Typography
              fontSize={{ xs: "1.05rem", sm: "1.35rem" }}
              fontWeight={800}
              color="#f4f8ff"
            >
              {computedTitle}
            </Typography>
          </Box>
        </Box>

        <Box
          ml={{ xs: 0, sm: "auto" }}
          width={{ xs: "100%", sm: "auto" }}
          display="flex"
          alignItems="center"
          justifyContent={{ xs: "space-between", sm: "flex-end" }}
          gap={1.25}
          flexWrap="wrap"
        >
          <Chip
            label={todayLabel}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              borderRadius: "12px",
              backgroundColor: "rgba(94,168,255,0.12)",
              color: "#cfe2ff",
              fontWeight: 700,
              border: "1px solid rgba(94,168,255,0.16)",
            }}
          />
          <Box
            px={1.6}
            py={1}
            borderRadius="16px"
            sx={{
              background: "linear-gradient(135deg, #101f34 0%, #133d30 100%)",
              color: "#f6fbff",
              border: "1px solid rgba(126, 170, 220, 0.12)",
              maxWidth: { xs: "100%", sm: "unset" },
            }}
          >
            <Typography fontSize="0.72rem" sx={{ opacity: 0.72 }}>
              Signed in as
            </Typography>
            <Typography fontSize="0.9rem" fontWeight={800}>
              {adminDetails?.name || "Admin"}
            </Typography>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  title: "",
};

DashboardNavbar.propTypes = {
  title: PropTypes.string,
};

export default DashboardNavbar;
